import React from 'react';
import styled, { useTheme } from 'styled-components';
import { applyTypography, TYPOGRAPHY_VARIANTS } from '../../styled/theme/typography-utils';
import PropTypes from 'prop-types';
import useStableId from '../../../hooks/useStableId';

const RadioContainer = styled.div`
    display: flex;
    align-items: center;
    position: relative;
`;

const Label = styled.label`
    display: flex;
    align-items: center;
    cursor: pointer;
    user-select: none;
`;

const VisuallyHiddenInput = styled.input`
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;

    &:focus-visible + div {
        outline: 2px solid ${({ theme }) => theme.components.lyt_radio.border.focus};
        outline-offset: 2px;
    }

    &:disabled + div {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const CustomRadio = styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== 'variant' && prop !== 'checked',
})`
    width: 14px;
    height: 14px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    border: 2px solid ${({ theme }) => theme.components.lyt_radio.border.default};
    transition: all 0.2s ease;
    position: relative;

    ${({ checked, theme }) => !checked && `
        background-color: ${theme.components.lyt_radio.background.default};
    `}

    ${({ checked, theme, variant }) => checked && variant === 'danger' && `
        border-color: ${theme.components.lyt_radio.border.danger};
        background-color: ${theme.components.lyt_radio.background.danger};
    `}

    ${({ checked, theme, variant }) => checked && variant !== 'danger' && `
        border-color: ${theme.components.lyt_radio.border.selected};
        background-color: ${theme.components.lyt_radio.border.selected};
    `}

    input:not(:disabled):not(:checked):hover + & {
        background-color: ${({ theme }) => theme.components.lyt_radio.background.hover};
    }

    input:not(:disabled):checked:hover + &[data-variant="danger"] {
        background-color: ${({ theme }) => theme.components.lyt_radio.background.danger};
    }

    input:not(:disabled):checked:hover + &:not([data-variant="danger"]) {
        background-color: ${({ theme }) => theme.components.lyt_radio.border.selected};
    }

    input:not(:disabled):not(:checked):active + & {
        background-color: ${({ theme }) => theme.components.lyt_radio.background.active};
    }

    input:not(:disabled):checked:active + &[data-variant="danger"] {
        background-color: ${({ theme }) => theme.components.lyt_radio.background.danger};
    }

    input:not(:disabled):checked:active + &:not([data-variant="danger"]) {
        background-color: ${({ theme }) => theme.components.lyt_radio.border.selected};
    }

    input:disabled + & {
        border-color: ${({ theme }) => theme.components.lyt_radio.border.disabled};
        background-color: ${({ theme }) => theme.components.lyt_radio.background.disabled};
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const Dot = styled.span`
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.components.lyt_radio.dot.selected};
`;

const LabelText = styled.span`
    margin-left: 8px;
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_1_NORMAL)}
`;

const LytRadio = ({
    id,
    name,
    label,
    checked,
    onChange,
    disabled = false,
    required = false,
    ariaLabel,
    ariaLabelledBy,
    ariaDescribedBy,
    className,
    testId,
    stopPropagation = false,
    value,
    variant = 'default',
}) => {
    const uniqueId = useStableId();
    const radioId = id || `radio-${uniqueId}`;
    const theme = useTheme();

    const handleChange = (e) => {
        if (stopPropagation) e.stopPropagation();
        onChange(e);
    };

    const handleLabelClick = (e) => {
        if (stopPropagation) e.stopPropagation();
    };

    const handleKeyDown = (e) => {
        // Allow Space or Enter to select the radio
        if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            if (!disabled) {
                onChange(e);
            }
        }
    };

    return (
        <RadioContainer className={className} data-testid={testId} onClick={stopPropagation ? e => e.stopPropagation() : undefined}>
            <Label htmlFor={radioId} onClick={handleLabelClick}>
                <VisuallyHiddenInput
                    type="radio"
                    id={radioId}
                    name={name}
                    checked={checked}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    disabled={disabled}
                    required={required}
                    aria-label={ariaLabel}
                    aria-labelledby={ariaLabelledBy}
                    aria-describedby={ariaDescribedBy}
                    value={value}
                    tabIndex={disabled ? -1 : 0}
                />
                <CustomRadio 
                    checked={checked} 
                    theme={theme} 
                    variant={variant}
                    data-variant={variant}
                >
                    {checked && <Dot theme={theme} />}
                </CustomRadio>
                {label && <LabelText>{label}</LabelText>}
            </Label>
        </RadioContainer>
    );
};

LytRadio.propTypes = {
    id: PropTypes.string,
    name: PropTypes.string,
    label: PropTypes.node,
    checked: PropTypes.bool.isRequired,
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    required: PropTypes.bool,
    ariaLabel: PropTypes.string,
    ariaLabelledBy: PropTypes.string,
    ariaDescribedBy: PropTypes.string,
    className: PropTypes.string,
    testId: PropTypes.string,
    stopPropagation: PropTypes.bool,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    variant: PropTypes.oneOf(['default', 'danger'])
};

export default LytRadio;


